// components/LanguageSelector/FlagLanguageSelector.js
import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { ChevronDown, Globe } from 'lucide-react';

const FlagLanguageSelector = ({ variant = 'desktop', position = 'bottom-right' }) => {
  const { language, changeLanguage, supportedLanguages, currentLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = async (langCode) => {
    if (language === langCode) {
      setIsOpen(false);
      return;
    }

    setIsChanging(true);
    try {
      await changeLanguage(langCode);
    } catch (error) {
      console.error('Error changing language:', error);
    } finally {
      setIsChanging(false);
      setIsOpen(false);
    }
  };

  const getPositionClasses = () => {
    switch (position) {
      case 'bottom-left':
        return 'left-0 origin-top-left';
      case 'bottom-right':
        return 'right-0 origin-top-right';
      case 'top-left':
        return 'left-0 bottom-full mb-2 origin-bottom-left';
      case 'top-right':
        return 'right-0 bottom-full mb-2 origin-bottom-right';
      default:
        return 'right-0 origin-top-right';
    }
  };

  // Desktop variant - compact with flags only
  if (variant === 'desktop') {
    return (
      <div className="relative inline-block text-left" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={isChanging}
          className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 transition-colors duration-200 border border-gray-200 bg-white disabled:opacity-50 cursor-pointer"
          title={currentLanguage.nativeName}
        >
          <span className="text-lg">{currentLanguage.flag}</span>
          <ChevronDown className="h-3 w-3 ml-1 text-gray-500" />
        </button>

        {isOpen && (
          <div className={`absolute ${getPositionClasses()} mt-2 w-48 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50`}>
            <div className="py-2">
              {Object.values(supportedLanguages).map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  disabled={isChanging}
                  className={`flex items-center w-full px-4 py-3 text-sm transition-colors duration-200 cursor-pointer ${
                    language === lang.code 
                      ? 'bg-[#253985] text-white border-r-2 border-[#253985]' 
                      : 'text-gray-700 hover:bg-gray-50'
                  } ${isChanging ? 'opacity-50' : ''}`}
                >
                  <span className="text-lg mr-3">{lang.flag}</span>
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{lang.nativeName}</span>
                    <span className={`text-xs ${language === lang.code ? 'text-gray-200' : 'text-gray-500'}`}>
                      {lang.name}
                    </span>
                  </div>
                  {language === lang.code && (
                    <div className="ml-auto w-2 h-2 bg-white rounded-full"></div>
                  )}
                  {isChanging && language === lang.code && (
                    <div className="ml-2 animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Mobile variant - full width with more details
  if (variant === 'mobile') {
    return (
      <div className="w-full" ref={dropdownRef}>
        <div className="border-t border-gray-200 pt-4 mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
            <Globe className="h-4 w-4 mr-2" />
            Language / Lingua
          </label>
          
          <div className="grid grid-cols-2 gap-3">
            {Object.values(supportedLanguages).map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                disabled={isChanging}
                className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                  language === lang.code 
                    ? 'border-[#253985] bg-[#253985] text-white shadow-sm' 
                    : 'border-gray-200 bg-white hover:border-gray-300'
                } ${isChanging ? 'opacity-50' : ''}`}
              >
                <span className="text-2xl mb-2">{lang.flag}</span>
                <span className="text-sm font-medium">
                  {lang.nativeName}
                </span>
                <span className={`text-xs mt-1 ${language === lang.code ? 'text-gray-200' : 'text-gray-500'}`}>
                  {lang.name}
                </span>
                {isChanging && language === lang.code && (
                  <div className="mt-2 animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Settings variant - for account settings page
  if (variant === 'settings') {
    return (
      <div className="w-full" ref={dropdownRef}>
        <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
          <Globe className="h-4 w-4 mr-2" />
          Language Preference
        </label>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Object.values(supportedLanguages).map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              disabled={isChanging}
              className={`flex items-start p-4 rounded-lg border-2 transition-all duration-200 text-left cursor-pointer ${
                language === lang.code 
                  ? 'border-[#253985] bg-[#253985] text-white shadow-sm' 
                  : 'border-gray-200 bg-white hover:border-gray-300'
              } ${isChanging ? 'opacity-50' : ''}`}
            >
              <span className="text-2xl mr-4 mt-1">{lang.flag}</span>
              <div className="flex-1">
                <div className="flex items-center">
                  <span className="font-medium">
                    {lang.nativeName}
                  </span>
                  {language === lang.code && (
                    <div className="ml-2 w-2 h-2 bg-white rounded-full"></div>
                  )}
                </div>
                <span className={`text-sm block mt-1 ${language === lang.code ? 'text-gray-200' : 'text-gray-500'}`}>
                  {lang.name}
                </span>
              </div>
              {isChanging && language === lang.code && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
            </button>
          ))}
        </div>
        
        <p className="text-sm text-gray-500 mt-3">
          Choose your preferred language for the application interface. This preference will be saved to your account.
        </p>
      </div>
    );
  }

  return null;
};

export default FlagLanguageSelector;